import { httpMethods } from '@/api';
import { sendApiRequest } from '@/api/sendApiRequest';
import {
  TExportToFileResponse,
  TFileUpload,
  TFileUploadInfo,
  TImportInfoResponse,
  TPagination,
  TResponseWithId,
} from '@/types';
import { TDocumentResponse, TDocumentsResponse } from '@/contexts/documentsContext/domain/types';
import { VEHICLE_UNIT_BY_TYPE } from '@/contexts/resourcesControlContext/domain/constants';
import { EContractsTableFilter } from '@partnershipsContext/components/ContractsTable/domain/constants';
import {
  EExecutorsLookupAutoPublishType,
  EAcceptanceMethod,
  EPartnershipResourcesDocumentStatusType,
  EPartnershipType,
} from '@partnershipsContext/domain/constants';
import { TFuncApplySetServiceParams } from '@/components/Filter/types';

import {
  TCreateContractRequest,
  TPartnershipDocumentRequest,
  TProlongContractRequest,
  TFetchPartnershipResourcesPayload,
  TExportContractsToExcelPayload,
  TChangePartnershipResourceStatusPayload,
  TEditRequirementsPayload,
  TAccreditationPayload,
  TRequirementsRequest,
  TPartnershipsRequest,
} from './types/requests';
import {
  TPartnershipGroupResponse,
  TIncomingInvitationsResponse,
  TContractResponse,
  TContractsResponse,
  TPartnershipsStatisticsResponse,
  TPartnershipStatisticsResponse,
  TPartnerCandidatesResponse,
  TPartnershipResponse,
  TContractTypesResponse,
  TContractCustomColumnsResponse,
  TContractCustomAttributesSchemaResponse,
  TContractCustomAttributesFormSchemaResponse,
  TPartnershipPartnerContactsResponse,
  TPartnershipsResponse,
  TUpdatePartnershipResourceStatusResponse,
  TPartnershipResourcesVehiclesResponse,
  TPartnershipResourcesDriversResponse,
  TPartnershipResourcesVehicleInfoResponse,
  TPartnershipResourcesDriverInfoResponse,
  TResponsibleEmployeesResponse,
  TPartnershipResourcesVehicleAuditEventsResponse,
  TPartnershipResourcesDriverAuditEventsResponse,
  TRequirementsResponse,
  TTenantRequirementsResponse,
  TPartnerAccreditationResponse,
  TTPartnerTenantInfoResponse,
} from './types/responses';

export const inviteToPartnership = (
  partnerId: string,
  initiatorRole: EPartnershipType,
) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: '/:tenant_id/outgoing_invitations',
  payload: {
    partnerId,
    initiatorRole,
  },
});

export const addExternalNumberForOutgoingInvitation = (
  invitationId: string,
  externalNumber: string,
) => sendApiRequest({
  method: httpMethods.PATCH,
  endpoint: `:tenant_id/outgoing_invitations/${invitationId}/external_number`,
  payload: { externalNumber },
});

export const acceptInvitation = (invitationId: string) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/incoming_invitations/${invitationId}/acceptance`,
});

export const rejectInvitation = (invitationId: string) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/incoming_invitations/${invitationId}/rejection`,
});

export const revokeInvitation = (invitationId: string) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/outgoing_invitations/${invitationId}/revocation`,
});

export const fetchIncomingInvitations = (
  { pageItems = 10, currentPage }: TPagination, searchQuery: string,
) => sendApiRequest<TIncomingInvitationsResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/incoming_invitations',
  params: {
    items: pageItems,
    page: currentPage,
    searchQuery,
  },
});

export const fetchOutgoingInvitations = (
  { pageItems = 10, currentPage }: TPagination, searchQuery: string,
) => sendApiRequest<TIncomingInvitationsResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/outgoing_invitations',
  params: {
    items: pageItems,
    page: currentPage,
    searchQuery,
  },
});

export const fetchPartnerCandidates = (
  query: string,
  role: string,
) => sendApiRequest<TPartnerCandidatesResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/autocomplete/partner_candidates',
  params: {
    query,
    role,
  },
});

export const fetchPartnerships = ({
  pagination, role, groupIds, searchQuery,
}: TPartnershipsRequest) => sendApiRequest<TPartnershipsResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/partnerships',
  params: {
    items: pagination.pageItems,
    page: pagination.currentPage,
    role,
    groupIds,
    searchQuery,
  },
});

export const fetchPartnershipInfo = (partnershipId: string) => sendApiRequest<TPartnershipResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships/${partnershipId}`,
});

export const fetchPartnershipsStatistics = () => sendApiRequest<TPartnershipsStatisticsResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/partnerships_statistics',
});

export const fetchPartnershipStatistics = ({ partnershipId }) => sendApiRequest<TPartnershipStatisticsResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/statistics`,
});

export const fetchVehiclesForPartnership = ({ type, pagination, partnershipId }: {
  type: any, pagination: TPagination, partnershipId: string,
}) => {
  const vehicleUnitType = VEHICLE_UNIT_BY_TYPE[type];
  if (!vehicleUnitType) return Promise.reject();

  return sendApiRequest({
    method: httpMethods.GET,
    endpoint: `/:tenant_id/partnerships/${partnershipId}/partnership_vehicles`,
    params: {
      items: pagination.pageItems,
      page: pagination.currentPage,
      vehicleUnitType,
    },
  });
};

export const fetchPartnershipDrivers = (
  partnershipId: string,
  {
    pageItems,
    currentPage,
  }: TPagination,
) => sendApiRequest({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/partnership_drivers`,
  params: {
    items: pageItems,
    page: currentPage,
  },
});

export const fetchPartnerGroups = () => sendApiRequest<TPartnershipGroupResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/partnerships/groups',
});

export const createPartnerGroup = (name: string) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: '/:tenant_id/partnerships/groups',
  params: { name },
});

export const editPartnerGroup = (groupId: string, name: string) => sendApiRequest({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/partnerships/groups/${groupId}`,
  params: { name },
});

export const deletePartnerGroup = (groupId: string) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/partnerships/groups/${groupId}`,
});

export const addPartnerToGroup = (groupId: string, partnerId: string) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/partnerships/${partnerId}/partnership_group`,
  params: { groupId },
});

export const removePartnerFromGroup = (groupId: string, partnerId: string) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/partnerships/${partnerId}/partnership_group`,
  params: { groupId },
});

/** Обновление заказчиком статуса ресурса (ТС) в партнерстве */
export const updatePartnershipVehicleClientStatus = ({
  resourceId,
  partnershipId,
  status,
  additionalParams,
}: TChangePartnershipResourceStatusPayload) => sendApiRequest<TUpdatePartnershipResourceStatusResponse>({
  method: httpMethods.PATCH,
  payload: {
    status,
    additionalParams,
  },
  endpoint: `/:tenant_id/partnerships/${partnershipId}/partnership_vehicles/${resourceId}/client_status`,
});

/** Обновление заказчиком статуса ресурса (водитель) в партнерстве */
export const updatePartnershipDriverClientStatus = ({
  resourceId,
  partnershipId,
  status,
  additionalParams,
}: TChangePartnershipResourceStatusPayload) => sendApiRequest<TUpdatePartnershipResourceStatusResponse>({
  method: httpMethods.PATCH,
  payload: {
    status,
    additionalParams,
  },
  endpoint: `/:tenant_id/partnerships/${partnershipId}/partnership_drivers/${resourceId}/client_status`,
});

/** Обновление исполнителем статуса своего ресурса (водитель) в партнерстве */
export const updatePartnershipVehicleServicerStatus = ({
  resourceId,
  partnershipId,
  status,
  additionalParams,
}: TChangePartnershipResourceStatusPayload) => sendApiRequest<TUpdatePartnershipResourceStatusResponse>({
  method: httpMethods.PATCH,
  payload: {
    status,
    additionalParams,
  },
  endpoint: `/:tenant_id/partnerships/${partnershipId}/partnership_vehicles/${resourceId}/servicer_status`,
});

/** Обновление исполнителем статуса своего ресурса (ТС) в партнерстве */
export const updatePartnershipDriverServicerStatus = ({
  resourceId,
  partnershipId,
  status,
  additionalParams,
}: TChangePartnershipResourceStatusPayload) => sendApiRequest<TUpdatePartnershipResourceStatusResponse>({
  method: httpMethods.PATCH,
  payload: {
    status,
    additionalParams,
  },
  endpoint: `/:tenant_id/partnerships/${partnershipId}/partnership_drivers/${resourceId}/servicer_status`,
});

export const pausePartnership = (partnershipId: string) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/pause`,
});

export const unpausePartnership = (partnershipId: string) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/pause`,
});

export const cancelPartnership = (partnershipId: string) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/cancellation`,
});

export const getPartnershipsUploadingUrl = (
  {
    partnerId,
    filename,
    type,
  }: {
    partnerId: string,
    filename: string,
    type: any,
  },
) => sendApiRequest({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships/${partnerId}/upload`,
  params: {
    filename,
    type,
  },
});

export const attachFileToPartnership = ({ file, partnerId }: { partnerId: string, file: any }) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/partnerships/${partnerId}/attachments`,
  payload: { file },
});

export const fetchPartnershipVehicleInformation = (
  {
    partnershipId,
    vehicleId,
  }: {
    vehicleId: string,
    partnershipId: string,
  },
) => sendApiRequest<TPartnershipResourcesVehicleInfoResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/partnership_vehicles/${vehicleId}`,
});

export const fetchPartnershipDriverInformation = (
  {
    partnershipId,
    driverId,
  }: {
    driverId: string,
    partnershipId: string,
  },
) => sendApiRequest<TPartnershipResourcesDriverInfoResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/partnership_drivers/${driverId}`,
});

export const fetchPartnershipResourceVehicle = (partnershipResourceId: string,
) => sendApiRequest<TPartnershipResourcesVehicleInfoResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships_vehicles/${partnershipResourceId}`,
});

export const fetchPartnershipResourceDriver = (partnershipResourceId: string,
) => sendApiRequest<TPartnershipResourcesDriverInfoResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships_drivers/${partnershipResourceId}`,
});

export const fetchPartnershipDocuments = (partnershipId: string) => sendApiRequest<TDocumentsResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/documents`,
});

export const uploadPartnershipDocuments = (
  partnershipId: string,
  payload: TPartnershipDocumentRequest,
) => sendApiRequest<TDocumentResponse>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/documents`,
  payload,
});

export const deletePartnershipDocument = (partnershipId: string, documentId: string) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/documents/${documentId}`,
});

export const fetchPartnerContacts = (
  partnershipId: string,
  {
    pageItems,
    currentPage,
  }: TPagination,
) => sendApiRequest<TPartnershipPartnerContactsResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/contacts`,
  params: {
    items: pageItems,
    page: currentPage,
  },
});

export const getContracts = (
  {
    pagination: {
      pageItems = 10,
      currentPage,
    },
    status = EContractsTableFilter.all,
    partnershipId,
    searchQuery = '',
  }: {
    pagination: TPagination,
    status: keyof typeof EContractsTableFilter,
    partnershipId?: string,
    searchQuery?: string,
  },
) => {
  const params: {
    items: number,
    page: number,
    status: keyof typeof EContractsTableFilter,
    partnershipId?: string,
    searchQuery?: string,
  } = {
    items: pageItems,
    page: currentPage,
    status,
  };

  if (partnershipId) {
    params.partnershipId = partnershipId;
  }

  if (searchQuery) {
    params.searchQuery = searchQuery;
  }

  return sendApiRequest<TContractsResponse>({
    method: httpMethods.GET,
    endpoint: '/:tenant_id/contracts',
    params,
  });
};

export const getContractTypes = () => sendApiRequest<TContractTypesResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/contracts/contract_types',
});

export const getContract = (
  { contractId }: { contractId: string },
) => sendApiRequest<TContractResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/contracts/${contractId}`,
});

export const archiveContract = (
  { contractId }: { contractId: string },
) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/contracts/${contractId}`,
});

export const unarchiveContract = (
  { contractId }: { contractId: string },
) => sendApiRequest<{ id: string }>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/contracts/${contractId}/recover`,
});

export const createContract = (
  { partnershipId, ...payload }: TCreateContractRequest,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/contracts`,
  payload,
});

export const editContract = (
  { partnershipId, ...payload }: TCreateContractRequest,
  contractId: string,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/contracts/${contractId}`,
  payload,
});

export const prolongContract = (contractId: string, payload: TProlongContractRequest) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/contracts/${contractId}/prolong`,
  payload,
});

export const updatePartnershipExternalNumber = (
  partnershipId: string,
  externalNumber: string,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/partnerships/${partnershipId}/external_number`,
  payload: { externalNumber },
});

export const updatePartnershipDefaultMarginPercent = (
  partnershipId: string,
  defaultMarginPercent: number | null,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/partnerships/${partnershipId}`,
  payload: {
    defaultMarginPercent,
    ...defaultMarginPercent === null ? { reductionAutoPublish: false } : {},
  },
});

export const getContractDocuments = (contractId: string) => sendApiRequest<TDocumentsResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/contracts/${contractId}/documents`,
});

export const uploadContractDocument = (
  contractId: string,
  fileData: TFileUploadInfo,
) => sendApiRequest<TDocumentResponse>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/contracts/${contractId}/documents`,
  payload: { file: fileData },
});

export const deleteContractDocument = (contractId: string, documentId: string) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/contracts/${contractId}/documents/${documentId}`,
});

export const getContractCustomAttributesSchema = () => sendApiRequest<TContractCustomAttributesSchemaResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/contracts/custom_attributes',
});

export const getContractCustomAttributesFormSchema = () => sendApiRequest<TContractCustomAttributesFormSchemaResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/contracts/custom_attributes/form_schema',
});

export const getContractCustomColumns = () => sendApiRequest<TContractCustomColumnsResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/contracts/custom_attributes/columns',
});

export const changeTargetOfferAcceptanceMethod = (
  partnershipId: string,
  targetOfferAcceptanceMethod: EAcceptanceMethod,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.PUT,
  payload: { targetOfferAcceptanceMethod },
  endpoint: `/:tenant_id/partnerships/${partnershipId}/target_offer_acceptance_method`,
});

export const changeExecutorsLookupAutoPublish = (
  partnershipId: string,
  executorsLookupAutoPublish: EExecutorsLookupAutoPublishType,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.PUT,
  payload: { executorsLookupAutoPublish },
  endpoint: `/:tenant_id/partnerships/${partnershipId}`,
});

export const getPartnershipsInvitationsTemplate = () => sendApiRequest<Blob>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/outgoing_invitations/template',
  responseType: 'blob',
  headers: { Accept: 'blob' },
});

export const getContractsTemplate = () => sendApiRequest<Blob>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/contracts/template',
  responseType: 'blob',
  headers: { Accept: 'blob' },
});

export const importOutgoingInvitations = ({ file }: TFileUpload) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: '/:tenant_id/outgoing_invitations/imports',
  params: { file },
});

export const getImportOutgoingInvitationsInfo = (id: string) => sendApiRequest<TImportInfoResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/outgoing_invitations/imports/${id}`,
});

export const exportPartnersToExcel = (groupIds: string[]) => sendApiRequest<TExportToFileResponse>({
  method: httpMethods.POST,
  endpoint: '/:tenant_id/partnerships/exports',
  payload: { groupIds },
});

export const exportContractsToExcel = (
  payload: TExportContractsToExcelPayload,
) => sendApiRequest<TExportToFileResponse>({
  method: httpMethods.POST,
  endpoint: '/:tenant_id/contracts/exports',
  payload,
});

export const importContracts = ({ file }: TFileUpload) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: '/:tenant_id/contracts/imports',
  params: { file },
});

export const getContractsImportInfo = (id: string) => sendApiRequest<TImportInfoResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/contracts/imports/${id}`,
});

export const fetchPartnershipResourcesVehicles = ({
  searchQuery = '',
  pagination,
  filterId,
}: TFetchPartnershipResourcesPayload) => sendApiRequest<TPartnershipResourcesVehiclesResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/partnerships_vehicles',
  params: {
    searchQuery,
    items: pagination.pageItems,
    page: pagination.currentPage,
    filterId,
  },
});

export const fetchPartnershipResourcesDrivers = ({
  searchQuery = '',
  pagination,
  filterId,
}: TFetchPartnershipResourcesPayload) => sendApiRequest<TPartnershipResourcesDriversResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/partnerships_drivers',
  params: {
    searchQuery,
    items: pagination.pageItems,
    page: pagination.currentPage,
    filterId,
  },
});

export const fetchVehiclesResponsibleEmployees = () => sendApiRequest<TResponsibleEmployeesResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/autocomplete/partnerships_vehicles/client_employees',
});

export const fetchDriversResponsibleEmployees = () => sendApiRequest<TResponsibleEmployeesResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/autocomplete/partnerships_drivers/client_employees',
});

export const saveFilterForPartnershipResourcesVehiclesTable = (
  { filters, operator }: TFuncApplySetServiceParams,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.POST,
  endpoint: '/:tenant_id/partnerships_vehicles/filters',
  payload: {
    filters,
    operator,
  },
});

export const saveFilterForPartnershipResourcesDriversTable = (
  { filters, operator }: TFuncApplySetServiceParams,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.POST,
  endpoint: '/:tenant_id/partnerships_drivers/filters',
  payload: {
    filters,
    operator,
  },
});

export const fetchPartnershipPopupInfo = (id: string) => sendApiRequest<TTPartnerTenantInfoResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships/public_partnership_popup_contacts/${id}`,
});

export const updatePartnershipDriverPrivateComment = (
  partnershipResourceId: string,
  comment: string,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PATCH,
  payload: { privateComment: comment },
  endpoint: `/:tenant_id/partnerships_drivers/${partnershipResourceId}/private_comment`,
});

export const updatePartnershipDriverPublicComment = (
  partnershipResourceId: string,
  comment: string,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PATCH,
  payload: { publicComment: comment },
  endpoint: `/:tenant_id/partnerships_drivers/${partnershipResourceId}/public_comment`,
});

export const updatePartnershipVehiclePrivateComment = (
  partnershipResourceId: string,
  comment: string,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PATCH,
  payload: { privateComment: comment },
  endpoint: `/:tenant_id/partnerships_vehicles/${partnershipResourceId}/private_comment`,
});

export const updatePartnershipVehiclePublicComment = (
  partnershipResourceId: string,
  comment: string,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PATCH,
  payload: { publicComment: comment },
  endpoint: `/:tenant_id/partnerships_vehicles/${partnershipResourceId}/public_comment`,
});

export const fetchPartnershipResourceVehicleAuditEvents = (partnershipResourceId: string,
) => sendApiRequest<TPartnershipResourcesVehicleAuditEventsResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships_vehicles/${partnershipResourceId}/audit_events`,
});

export const fetchPartnershipResourceDriverAuditEvents = (partnershipResourceId: string,
) => sendApiRequest<TPartnershipResourcesDriverAuditEventsResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships_drivers/${partnershipResourceId}/audit_events`,
});

export const updatePartnershipResourceDriverDocumentStatuses = (
  {
    partnershipResourceId,
    documentId,
    status,
  }: { partnershipResourceId: string, documentId: string, status: EPartnershipResourcesDocumentStatusType },
) => sendApiRequest<{ id: string }>({
  method: httpMethods.POST,
  payload: { status },
  endpoint: `/:tenant_id/partnerships_drivers/${partnershipResourceId}/documents/${documentId}/statuses`,
});

export const updatePartnershipResourceVehicleDocumentStatuses = (
  {
    partnershipResourceId,
    documentId,
    status,
  }: { partnershipResourceId: string, documentId: string, status: EPartnershipResourcesDocumentStatusType },
) => sendApiRequest<{ id: string }>({
  method: httpMethods.POST,
  payload: { status },
  endpoint: `/:tenant_id/partnerships_vehicles/${partnershipResourceId}/documents/${documentId}/statuses`,
});

export const editAccreditationQuestionnaire = (payload: TRequirementsRequest) => sendApiRequest({
  method: httpMethods.PATCH,
  endpoint: '/:tenant_id/accreditation_questionnaire',
  payload,
});

export const getAccreditationQuestionnaire = () => sendApiRequest<TRequirementsResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/accreditation_questionnaire',
});

export const getTenantAccreditationQuestionnaire = (tenantId: string) => sendApiRequest<TTenantRequirementsResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partner_candidates/${tenantId}/accreditation_questionnaire`,
});

export const createAccreditationQuestionnaire = (payload: TAccreditationPayload) => sendApiRequest<{ id: string }>({
  method: httpMethods.POST,
  endpoint: '/:tenant_id/outgoing_invitations',
  payload,
});

export const getPartnerAccreditation = (id: string) => sendApiRequest<TPartnerAccreditationResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/partnerships/${id}/accreditation`,
});

export const editPartnerAccreditation = (
  id: string,
  payload: TEditRequirementsPayload,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/partnerships/${id}/accreditation`,
  payload,
});

export const getOutgoingAccreditation = (id: string) => sendApiRequest<TPartnerAccreditationResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/outgoing_invitations/${id}/accreditation`,
});

export const getIncomingInvitation = (id: string) => sendApiRequest<TPartnerAccreditationResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/incoming_invitations/${id}/accreditation`,
});

export const editOutgoingAccreditation = (
  id: string,
  payload: TEditRequirementsPayload,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/outgoing_invitations/${id}/accreditation`,
  payload,
});

export const fillPartnerAccreditation = (
  id: string,
  payload: TEditRequirementsPayload,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/partnerships/${id}/accreditation`,
  payload,
});
